/** Docs (UA): see modules/game/DOCS.md — canvas renderer for the play field */

import {
    GAME_CONFIG as C,
    GAME_PALETTE as P,
    GameTheme,
    ItemKind,
    rgbaFromHex as rgba,
} from '../game.constants';
import { bugX, GameState } from './engine';
import { FIELD, FieldLayout } from './layout';
import { SpriteSet } from './sprites';

interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
    maxLife: number;
    color: string;
    size: number;
    round: boolean;
}

interface Popup {
    x: number;
    y: number;
    text: string;
    gold: boolean;
    life: number;
}

interface TrailDot {
    x: number;
    y: number;
    life: number;
}

export interface FxState {
    particles: Particle[];
    popups: Popup[];
    trail: TrailDot[];
    squashUntil: number;
    shakeUntil: number;
    time: number;
}

export const createFx = (): FxState => ({
    particles: [],
    popups: [],
    trail: [],
    squashUntil: -1,
    shakeUntil: -1,
    time: 0,
});

const PARTICLE_CAP = 48;

export const fxCatchBurst = (fx: FxState, x: number, y: number, kind: ItemKind, score: number): void => {
    const color = kind === 'gold' ? P.gold : kind === 'mult' ? P.accent : P.cream;
    for (let i = 0; i < 8 && fx.particles.length < PARTICLE_CAP; i += 1) {
        const a = Math.PI + (i / 7) * Math.PI;
        const speed = 90 + Math.random() * 120;
        fx.particles.push({
            x,
            y,
            vx: Math.cos(a) * speed,
            vy: Math.sin(a) * speed - 60,
            life: 0.5,
            maxLife: 0.5,
            color,
            size: 3 + Math.random() * 3,
            round: i % 2 === 0,
        });
    }
    if (score > 0) {
        fx.popups.push({ x, y: y - 80, text: `+${score}`, gold: kind === 'gold' || score > C.SCORE.gold, life: 0.5 });
    }
    fx.squashUntil = fx.time + 0.08;
};

export const fxHitBurst = (fx: FxState, x: number, y: number): void => {
    for (let i = 0; i < 5 && fx.particles.length < PARTICLE_CAP; i += 1) {
        fx.particles.push({
            x,
            y,
            vx: (Math.random() - 0.5) * 260,
            vy: -80 - Math.random() * 140,
            life: 0.45,
            maxLife: 0.45,
            color: i % 2 === 0 ? P.bugBody : P.cream,
            size: 3 + Math.random() * 4,
            round: false,
        });
    }
    fx.shakeUntil = fx.time + 0.15;
};

/** Sheens and crumbs sit at fractions of the floor band, not at fixed offsets:
 *  the band breathes with the viewport, and a hardcoded 58 fell off a short one. */
const FLOOR_SHEENS: Array<[number, number, number]> = [
    [196, 104, 0.357],
    [104, 62, 0.548],
    [286, 48, 0.69],
];
const FLOOR_CRUMBS: Array<[number, number, number]> = [
    [64, 0.286, 1.8], [128, 0.571, 1.2], [232, 0.381, 1.5], [300, 0.714, 1.8],
    [348, 0.429, 1.2], [172, 0.81, 1.4], [258, 0.881, 1.6],
];

/** The counter the catcher stands on. A flat slab read as a sticker pasted
 *  over the sky, so the surface gets four things a real one has: depth, a lit
 *  edge with the shadow it casts, the ember's warm spill, and coffee crumbs. */
const renderFloor = (ctx: CanvasRenderingContext2D, layout: FieldLayout): void => {
    const top = layout.puddleY;
    const floorH = layout.worldH - top;

    const surface = ctx.createLinearGradient(0, top, 0, layout.worldH);
    surface.addColorStop(0, P.slab);
    surface.addColorStop(1, P.slabDeep);
    ctx.fillStyle = surface;
    ctx.fillRect(0, top, FIELD.refW, floorH);

    // Shadow the edge throws down the front panel.
    const lip = ctx.createLinearGradient(0, top, 0, top + 14);
    lip.addColorStop(0, rgba(P.ink, 0.3));
    lip.addColorStop(1, rgba(P.ink, 0));
    ctx.fillStyle = lip;
    ctx.fillRect(0, top, FIELD.refW, 14);

    // Long, low sheens — a polished counter, not puddles.
    ctx.fillStyle = rgba(P.cream, 0.04);
    for (const [cx, rx, f] of FLOOR_SHEENS) {
        ctx.beginPath();
        ctx.ellipse(cx, top + f * floorH, rx, 3.5, 0, 0, Math.PI * 2);
        ctx.fill();
    }

    ctx.fillStyle = rgba(P.bokeh2, 0.45);
    for (const [cx, f, r] of FLOOR_CRUMBS) {
        ctx.beginPath();
        ctx.arc(cx, top + f * floorH, r, 0, Math.PI * 2);
        ctx.fill();
    }

    // Warm light goes ON TOP: under the edge shadow it left the counter cold
    // against a coffee world. Band = the whole lit strip the sky throws down,
    // spill = the brighter pool right below the ember.
    const band = ctx.createLinearGradient(0, top, 0, top + 30);
    band.addColorStop(0, rgba(P.ember, 0.22));
    band.addColorStop(1, rgba(P.ember, 0));
    ctx.fillStyle = band;
    ctx.fillRect(0, top, FIELD.refW, 30);

    const spill = ctx.createRadialGradient(0, 0, 8, 0, 0, 200);
    spill.addColorStop(0, rgba(P.ember, 0.4));
    spill.addColorStop(1, rgba(P.ember, 0));
    ctx.save();
    ctx.translate(195, top + 12);
    ctx.scale(1, 56 / 200);
    ctx.fillStyle = spill;
    ctx.beginPath();
    ctx.arc(0, 0, 200, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Lit front edge: warm where the light falls, cool at the rims.
    const edge = ctx.createLinearGradient(0, 0, FIELD.refW, 0);
    edge.addColorStop(0, rgba(P.cream, 0.05));
    edge.addColorStop(0.5, rgba(P.ember, 0.55));
    edge.addColorStop(1, rgba(P.cream, 0.05));
    ctx.fillStyle = edge;
    ctx.fillRect(0, top, FIELD.refW, 2.5);
};

/** Bokeh as fractions of the band between canopy and counter — the mockup's
 *  844-tall positions divided by that band, so the depth reads the same on a
 *  screen 200px shorter instead of bunching up under the canopy. */
const BOKEH: Array<[number, number, number, string, number]> = [
    [60, 0.151, 22, P.bokeh1, 0.16], [310, 0.245, 14, P.bokeh2, 0.2],
    [130, 0.371, 28, P.bokeh1, 0.12], [355, 0.481, 18, P.bokeh2, 0.14],
    [70, 0.638, 12, P.bokeh1, 0.22], [250, 0.575, 24, P.bokeh2, 0.12],
    [340, 0.764, 15, P.bokeh1, 0.18], [110, 0.843, 20, P.bokeh2, 0.13],
    [190, 0.198, 10, P.bokeh2, 0.22], [30, 0.497, 16, P.bokeh1, 0.15],
    [290, 0.906, 13, P.bokeh1, 0.2], [160, 0.701, 9, P.bokeh2, 0.22],
];

let backdropCache: { key: string; canvas: HTMLCanvasElement } | null = null;

/** Static field backdrop: sky, bokeh, ember, counter, canopy. Cached by layout —
 *  старт-екран і ігровий цикл малювали його двічі за монт, а кожен resize
 *  перебудовував наново, хоч результат залежить тільки від розкладки. */
export const renderBackground = (layout: FieldLayout): HTMLCanvasElement => {
    const key = `${Math.round(FIELD.refW * layout.scale)}x${Math.round(layout.worldH * layout.scale)}@${Math.round(layout.canopyH)}`;
    if (backdropCache && backdropCache.key === key) return backdropCache.canvas;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(FIELD.refW * layout.scale);
    canvas.height = Math.round(layout.worldH * layout.scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Game background: 2d context unavailable');
    // One scale, and the whole backdrop is drawn in ref units from here on.
    ctx.scale(layout.scale, layout.scale);

    const sky = ctx.createLinearGradient(0, 0, 0, layout.worldH);
    sky.addColorStop(0, P.skyTop);
    sky.addColorStop(1, P.skyBottom);
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, FIELD.refW, layout.worldH);

    const band = layout.puddleY - layout.canopyH;
    for (const [x, f, r, color, alpha] of BOKEH) {
        ctx.globalAlpha = alpha;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, layout.canopyH + f * band, r, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Ember spotlight BEHIND the catcher, anchored to the counter. Alpha 0.30
    // is a hard art limit: 0.55 drops token contrast to 1.7:1 in the decision zone.
    const ember = ctx.createRadialGradient(0, 0, 10, 0, 0, 170);
    ember.addColorStop(0, rgba(P.ember, 0.3));
    ember.addColorStop(1, rgba(P.ember, 0));
    ctx.save();
    ctx.translate(195, layout.puddleY + 40);
    ctx.scale(1, 220 / 340); // spec: 340×220 ellipse, not a circle
    ctx.fillStyle = ember;
    ctx.beginPath();
    ctx.arc(0, 0, 170, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    renderFloor(ctx, layout);

    // Glaze canopy with scalloped edge; the DOM HUD lives on top of it.
    ctx.fillStyle = P.slab;
    ctx.fillRect(0, 0, FIELD.refW, layout.canopyH);
    for (let i = 0; i < 6; i += 1) {
        ctx.beginPath();
        ctx.arc(32 + i * 62 + 10, layout.canopyH, FIELD.scallopR, 0, Math.PI * 2);
        ctx.fill();
    }
    backdropCache = { key, canvas };
    return canvas;
};

const itemSprite = (sprites: SpriteSet, kind: ItemKind): HTMLCanvasElement => {
    if (kind === 'gold') return sprites.golden;
    if (kind === 'bug') return sprites.bug;
    if (kind === 'mult') return sprites.mult;
    return sprites.item;
};

/** Кадрові константи: rgba()-рядки і присвоєння ctx.font коштують парсингу,
 *  а в циклі кадру вони не змінюються. */
const SHADOW_INK = rgba(P.ink, 0.45);
const MULT_FONT = '900 11px Unbounded, sans-serif';
const POPUP_FONT = '800 20px Commissioner, sans-serif';
const SIZE_KEY: Record<ItemKind, 'item' | 'golden' | 'bug' | 'mult'> = {
    seed: 'item',
    gold: 'golden',
    bug: 'bug',
    mult: 'mult',
};

/** Згасання на місці: щокадрові .filter() створювали три нові масиви на кадр,
 *  тобто рівний потік сміття для GC у найгарячішому циклі гри. */
const decay = <T extends { life: number }>(list: T[], dt: number): void => {
    let write = 0;
    for (let read = 0; read < list.length; read += 1) {
        const entry = list[read];
        entry.life -= dt;
        if (entry.life > 0) {
            list[write] = entry;
            write += 1;
        }
    }
    list.length = write;
};

export interface DrawOptions {
    theme: GameTheme;
    fontsReady: boolean;
}

export const drawFrame = (
    ctx: CanvasRenderingContext2D,
    background: HTMLCanvasElement,
    sprites: SpriteSet,
    state: GameState,
    fx: FxState,
    dt: number,
    layout: FieldLayout,
): void => {
    fx.time += dt;
    const w = FIELD.refW;

    ctx.save();
    // Everything below is drawn in ref units; the stage sits centred when the
    // viewport is wider than the world (tablet, desktop, landscape).
    ctx.setTransform(layout.scale, 0, 0, layout.scale, layout.offsetX * layout.dpr, 0);

    // Screen shake only on the background layer — the catcher stays glued
    // to the finger, zero control desync.
    const shaking = fx.time < fx.shakeUntil;
    const shakeX = shaking ? (Math.random() - 0.5) * 8 : 0;
    ctx.drawImage(background, shakeX, 0, w, layout.worldH);

    // Golden-rain finale: the sky warms up over the last 10 seconds.
    if (state.time > 50) {
        ctx.globalAlpha = Math.min((state.time - 50) / 10, 1) * 0.08;
        ctx.fillStyle = P.gold;
        ctx.fillRect(0, 0, w, layout.worldH);
        ctx.globalAlpha = 1;
    }

    const fieldH = layout.catchY - layout.spawnY;

    // Trail dots (gold): short fading dots, never long streaks.
    decay(fx.trail, dt);
    for (const dot of fx.trail) {
        ctx.globalAlpha = (dot.life / 0.3) * 0.8;
        ctx.fillStyle = P.goldGlow;
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, 2.5, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.globalAlpha = 1;

    for (const item of state.items) {
        const py = layout.spawnY + item.y * fieldH;
        const x = item.kind === 'bug' ? bugX(item, state.time) * w : item.x * w;
        const sprite = itemSprite(sprites, item.kind);
        const size = sprites.sizes[SIZE_KEY[item.kind]];
        // Fade-scale-in during the first 120 ms after emerging from the canopy.
        const age = state.time - item.born;
        const appear = Math.min(age / 0.12, 1);
        ctx.save();
        ctx.translate(x, py);
        ctx.globalAlpha = appear;
        const spin =
            item.kind === 'gold' ? (age * 90) % 360
            : item.kind === 'mult' ? (age * 30) % 360
            : item.rotation;
        ctx.rotate((spin * Math.PI) / 180);
        const drawSize = size * (0.9 + 0.1 * appear);
        ctx.drawImage(sprite, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
        ctx.restore();
        if (item.kind === 'gold' && Math.random() < dt * 12 && fx.trail.length < 12) {
            fx.trail.push({ x, y: py - 20, life: 0.3 });
        }
        if (item.kind === 'mult') {
            // Counter-rotated ×2 label: always upright over the spinning disc.
            ctx.save();
            ctx.translate(x, py);
            ctx.globalAlpha = appear;
            ctx.fillStyle = P.ink;
            if (ctx.font !== MULT_FONT) ctx.font = MULT_FONT;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('×2', 0, -3);
            ctx.restore();
        }
    }

    // Catcher with tilt toward movement + squash on catch.
    const cupW = 96;
    const cupH = 64;
    const cupPx = state.cupX * w;
    const tilt = Math.max(Math.min((state.targetX - state.cupX) * 40, 6), -6);
    const squash = fx.time < fx.squashUntil;
    const blink = state.time < state.iframesUntil && Math.floor(state.time * 10) % 2 === 0;
    // Contact shadow on the counter — without it the catcher floats above it.
    ctx.fillStyle = SHADOW_INK;
    ctx.beginPath();
    ctx.ellipse(cupPx, layout.puddleY + 4, squash ? 39 : 34, 6, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.save();
    ctx.translate(cupPx, layout.catchY);
    ctx.rotate((tilt * Math.PI) / 180);
    if (blink) ctx.globalAlpha = 0.45;
    const sx = squash ? 1.15 : 1;
    const sy = squash ? 0.85 : 1;
    ctx.drawImage(sprites.catcher, (-cupW / 2) * sx, -cupH / 2 - (squash ? -4 : 0), cupW * sx, cupH * sy);
    ctx.restore();

    decay(fx.particles, dt);
    for (const p of fx.particles) {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 500 * dt;
        ctx.globalAlpha = Math.max(p.life / p.maxLife, 0);
        ctx.fillStyle = p.color;
        if (p.round) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
            ctx.fill();
        } else {
            ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size * 0.7);
        }
    }
    ctx.globalAlpha = 1;

    decay(fx.popups, dt);
    // Rubik більше не вантажиться (гібридна типографіка) — поп-апи малювались
    // фолбеком; тепер це той самий Commissioner, що й решта інтерфейсу.
    if (fx.popups.length > 0 && ctx.font !== POPUP_FONT) ctx.font = POPUP_FONT;
    for (const p of fx.popups) {
        const k = 1 - p.life / 0.5;
        ctx.globalAlpha = Math.min(p.life / 0.2, 1);
        ctx.fillStyle = p.gold ? P.gold : P.cream;
        ctx.textAlign = 'center';
        ctx.fillText(p.text, p.x, p.y - k * 40);
    }
    ctx.globalAlpha = 1;
    ctx.restore();
};
